import { db } from '../../db/indexeddb'
import {
  getCloudTaskReminders,
  getCloudTaskReminderIds,
  getCloudTaskRemindersByIds,
  getDeletedCloudTaskReminders,
  createCloudTaskReminder,
  updateCloudTaskReminder,
  deleteCloudTaskReminder,
} from '../../supabase/queries'
import { CloudTaskReminderSchema, CloudTaskReminderPayloadSchema } from '../sync-schemas'
import { resolveCloudTaskId, resolveLocalTaskId } from '../sync-resolve'
import type { PullDescriptor } from '../sync-pull'
import type { PushDescriptor } from '../sync-push'
import type { RecurrenceRule, TaskReminder } from '../../db/models'

/* ── TASK REMINDERS (Erinnerungen, Plan task-reminders) ─────────────────── */

/** Die Arten, die diese Version kennt; eine andere (spätere Version, Mobil-App) bleibt in der Cloud. */
const KNOWN_KINDS = new Set(['absolute', 'relative'])

/** Die Felder, die eine Erinnerung ausmachen — beim Anlegen wie beim Ändern dieselben. */
function fieldsOf(remote: any) {
  return {
    kind: remote.kind,
    anchor: remote.anchor ?? null,
    offsetMinutes: remote.offset_minutes ?? null,
    remindDate: remote.remind_date ?? null,
    remindTime: remote.remind_time ?? null,
    recurrence: (remote.recurrence ?? null) as RecurrenceRule | null,
    snoozedUntil: remote.snoozed_until ? new Date(remote.snoozed_until) : null,
  }
}

export const taskReminderPullDescriptor: PullDescriptor<any> = {
  // `entityName + 's'` ist der Name in den Log-Zeilen; die Warteschlange heißt wie die Tabelle.
  entityName: 'taskReminder',
  settingsKey: 'lastTaskReminderSyncTime',

  fetchRemote: (userId, since) => getCloudTaskReminders(userId, since),
  fetchRemoteIds: (userId) => getCloudTaskReminderIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudTaskRemindersByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudTaskReminders(userId, since),

  safeParse: (raw) => CloudTaskReminderSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.taskReminders,
  getAllLocal: () => db.taskReminders.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.taskReminders.filter(r => !r.deletedAt).count()
    return count === 0
  },

  buildLocalInsert: async (remote) => {
    if (!KNOWN_KINDS.has(remote.kind)) return null
    const localTaskId = await resolveLocalTaskId(remote.task_id, remote.task_local_id)
    // Die Aufgabe ist noch nicht auf diesem Gerät — der Task-Pull läuft vorher und legt sie an.
    // Fehlt sie trotzdem, bleibt die Zeile übersprungen (`rememberSkippedCloudId`): Sie kommt erst,
    // wenn sie sich in der Cloud ändert oder der Abgleich der Kennungen nach einem Neustart läuft.
    if (!localTaskId) return null
    return {
      id: crypto.randomUUID(),
      taskId: localTaskId,
      ...fieldsOf(remote),
      importedFrom: remote.imported_from ?? null,
      userId: remote.user_id,
      cloudId: remote.id,
      deletedAt: null,
      createdAt: new Date(remote.created_at),
      updatedAt: new Date(remote.updated_at),
      lastModified: new Date(remote.updated_at),
      synced: true,
      syncedAt: new Date(),
    }
  },

  buildLocalUpdate: async (remote) => {
    // Unbekannte Art: Die Zeile hier bleibt, wie sie ist — falsch übernommen klänge sie falsch.
    if (!KNOWN_KINDS.has(remote.kind)) return null
    // **Umgehängt** (Wiederkehr, ADR 0021): Ist der Nachfolger nicht hier (sein Pull scheiterte), bleibt
    // sie an der alten Aufgabe — und zwar, bis sie sich in der Cloud wieder ändert: Die Zeile gilt
    // danach als abgeglichen und wird nicht neu geholt.
    const localTaskId = await resolveLocalTaskId(remote.task_id, remote.task_local_id)
    return {
      ...(localTaskId ? { taskId: localTaskId } : {}),
      ...fieldsOf(remote),
      ...('imported_from' in remote ? { importedFrom: remote.imported_from ?? null } : {}),
      updatedAt: new Date(remote.updated_at),
      deletedAt: remote.deleted_at ? new Date(remote.deleted_at) : null,
    }
  },
}

export const taskReminderPushDescriptor: PushDescriptor<TaskReminder> = {
  entityName: 'CloudTaskReminder',
  getLocal: async (id) => (await db.taskReminders.get(id)) ?? null,
  getLocalCloudId: (l) => l.cloudId,
  getLocalId: (l) => l.id,
  table: db.taskReminders,

  // Eine Erinnerung kann nicht vor ihrer Aufgabe in der Cloud stehen — sie wartet auf deren Kennung.
  canCreate: async (reminder) => {
    const cloudTaskId = await resolveCloudTaskId(reminder.taskId)
    return cloudTaskId ? { defer: false } : { defer: true }
  },

  // Auch beim Umhängen: Der Nachfolger muss oben sein, bevor die Erinnerung auf ihn zeigt.
  canUpdate: async (reminder) => {
    const cloudTaskId = await resolveCloudTaskId(reminder.taskId)
    return cloudTaskId ? { defer: false } : { defer: true }
  },

  buildCreatePayload: async (reminder) => payloadOf(reminder),

  buildUpdatePayload: async (reminder, queued) => {
    const payload: Record<string, any> = await payloadOf(reminder)
    // Das Rückgängig einer Löschung belebt die Zeile wieder — die `null` muss ausdrücklich mit.
    if (!reminder.deletedAt) payload.deleted_at = null
    /*
      **Die Aufgabe nur beim Umhängen** (Diff-Audit 30.09.2026): Schlummert ein Gerät, das den
      neuesten Stand noch nicht hat, eine Erinnerung, trüge die ganze Zeile ihre ALTE Aufgabe hoch —
      und hängte sie an die erledigte Runde zurück, von der ein anderes Gerät sie längst an den
      Nachfolger gehängt hat. Einträge ohne das Kennzeichen (auch ältere) schicken sie weiter mit.
    */
    if (queued?.taskUnchanged) {
      delete payload.task_id
      delete payload.task_local_id
    }
    return payload
  },

  createCloud: (userId, payload) => createCloudTaskReminder(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudTaskReminder(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudTaskReminder(cloudId, userId),
}

/**
 * Alle Felder, immer — auch die `null`en: Ein beendetes Schlummern (`snoozed_until: null`) oder
 * ein Wechsel von fest zu relativ muss oben ankommen (Sync-Checkliste Punkt 17).
 */
async function payloadOf(reminder: TaskReminder) {
  const cloudTaskId = await resolveCloudTaskId(reminder.taskId)
  return CloudTaskReminderPayloadSchema.parse({
    task_id: cloudTaskId,
    task_local_id: reminder.taskId,
    kind: reminder.kind,
    anchor: reminder.anchor,
    offset_minutes: reminder.offsetMinutes,
    remind_date: reminder.remindDate,
    remind_time: reminder.remindTime,
    recurrence: reminder.recurrence,
    snoozed_until: reminder.snoozedUntil ? reminder.snoozedUntil.toISOString() : null,
    imported_from: reminder.importedFrom ?? null,
    local_id: reminder.id,
  })
}
